import React, { useState } from 'react';
import { Box, Typography, FormControl, Divider } from '@mui/material';
import { getApolloDiseaseDetection, setApolloDiseaseDetectionGapStatus, setApolloDiseaseDetectionRecStatus } from '../../api';
import useFetch from '../../hooks/useFetch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Feedback from './Feedback';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from '@mui/styles';
import { Dialog, DialogContent, DialogTitle, DialogActions, TableHead } from '@mui/material';

const CoralDiseaseDetection = ({ patientId, username, token }) => {
    const [reload, setReload] = useState(true);
    const { data: patient, loading, error } = useFetch(getApolloDiseaseDetection, patientId, { username, token, reload });
    const [hccGaps, setHccGaps] = React.useState([]);
    const [hccCoding, setHccCoding] = React.useState([]);
    const [hccRecaptures, setHccRecaptures] = React.useState([]);
    const [hccRecommendations, setHccRecommendations] = React.useState([]);
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
    const [feedbackConfig, setFeedbackConfig] = useState({});
    const [openNotesModal, setOpenNotesModal] = useState(false);  
    const [activeFeedbacks, setActiveFeedbacks] = useState([]);
    const [openEvidenceModal, setOpenEvidenceModal] = useState(false);
    const [activeEvidences, setActiveEvidences] = useState([]);
    const [activeDiagnosisHeader, setActiveDiagnosisHeader] = useState('');
  
    const useStyles = makeStyles({
        tooltip: {
          fontSize: "1em",
        },
        hoverableText: {
          cursor: 'pointer', // Makes it clear the text is interactive
          textDecoration: 'underline', // Adds an underline for hoverable text
        },
    });
    const classes = useStyles();

    const handleCloseFeedback = () => {
      setIsFeedbackOpen(false);
      setReload(!reload);
    };

    const handleViewNotes = (feedbacks) => {
      setActiveFeedbacks(feedbacks);
      setOpenNotesModal(true);
    };
    
    const handleCloseNotesModal = () => {
        setOpenNotesModal(false);
        setActiveFeedbacks([]);
    };

    const handleViewEvidenceModal = (evidences, headerLabel) => {
      setActiveEvidences(evidences);
      setActiveDiagnosisHeader(headerLabel);
      setOpenEvidenceModal(true);
    };

    const handleCloseEvidenceModal = () => {
      setOpenEvidenceModal(false);
      setActiveEvidences([]);
      setActiveDiagnosisHeader('');
    };

    React.useEffect(() => {
        if (patient && patient.HccGaps) {
            setHccGaps(patient.HccGaps);
        }
        if (patient && patient.HccRecommendations) {
            setHccRecommendations(patient.HccRecommendations);
        }
        if (patient && patient.Coding) {
          setHccCoding(patient.Coding);
        }
        if (patient && patient.HccRecaptures) {
          setHccRecaptures(patient.HccRecaptures);
        }
    }, [patient,reload]);

    if (loading) return <Typography>Loading...</Typography>;
    if (error) return <Typography>Error: {error.message}</Typography>;

    const handleAckGaps = (event, diagnosisCode) => {
        setApolloDiseaseDetectionGapStatus(patientId, { username, token}, diagnosisCode, 'ACK');
        setHccGaps(prevState => prevState.map(gap =>
            gap.DiagnosisCode === diagnosisCode ? { ...gap, Status: 'ACK' } : gap
        ));
    };

    const handleChangeGaps = (event, diagnosisCode) => {
        const { value } = event.target;
        setApolloDiseaseDetectionGapStatus(patientId, { username, token}, diagnosisCode, value);
        setHccGaps(prevState => prevState.map(gap =>
            gap.DiagnosisCode === diagnosisCode ? { ...gap, Status: value } : gap
        ));
    };

    const handleAckCoding = (event, messageId) => {
      setFeedbackConfig({
        title: 'Resolve Coder Suggestion',
        feedback_description: 'Additional Information',
        module_category: 'DiseaseDetection',
        module_name: 'Coding',
        module_item_key: messageId,
      });
      setIsFeedbackOpen(true);
    };

    const handleAckRecommendations = (event, diagnosisCode) => {
      setApolloDiseaseDetectionRecStatus(patientId, { username, token}, diagnosisCode, 'ACK');
      setHccRecommendations(prevState => prevState.map(rec =>
        rec.DiagnosisCode === diagnosisCode ? { ...rec, Status: 'ACK' } : rec
      ));
    };

    const handleChangeRecommendations = (event, diagnosisCode) => {
        const { value } = event.target;
        setApolloDiseaseDetectionRecStatus(patientId, { username, token}, diagnosisCode, value);
        setHccRecommendations(prevState => prevState.map(rec =>
            rec.DiagnosisCode === diagnosisCode ? { ...rec, Status: value } : rec
        ));
    };

    if (patient.ActiveGap === 0 && patient.ActiveRec === 0 && patient.ActiveCoding == 0 && patient.ActiveRecap === 0) {
      return (<div></div>)
    }

    let gapComponent = ""
    let recCompoent = ""
    let codingComponent = ""
    let hasInfo = 0
    if (patient.ActiveGap === 1 || patient.ActiveRecap === 1) {
        gapComponent = (<div><Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            bgcolor="#b8503a"
            color="white"
            p={1}
            borderRadius="4px 4px 0 0"
        >
            HCC Gaps
        </Box>
        <TableContainer component={Paper}>
            <Table aria-label="simple table">
                <TableBody>
                    {hccGaps.map(gap => (
                        <TableRow key={gap.MessageId} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell sx={{ padding: '4px' }} component="th" scope="row">
                            <Tooltip title={gap.DiagnosisDescription} arrow classes={{tooltip: classes.tooltip}}><span className={classes.hoverableText}>{gap.DiagnosisCode}</span></Tooltip> (HCC {gap.HCC}): {gap.Description}<br /><span style={{ fontSize: '0.8em', color: '#555555' }}>Last Assessed: {gap.LastAssessedDate}</span>
                            </TableCell>
                        </TableRow>
                    ))}

                    {hccRecaptures.map((recaps, i) => (
                      <React.Fragment key={i}>
                      {
                       recaps.map((recap) => { 
                          
                          if (recap.ModuleName === "HccRecapture") {
                            const latestEvidence = recap.Evidences
                              .map(e => new Date(e.detected_date))
                              .sort((a, b) => b - a)[0]?.toISOString().split('T')[0];
                            return (
                              <React.Fragment key={recap.MessageId}>
                                {/* Spacer Row */}
                                <TableRow>
                                  <TableCell colSpan={2} sx={{ padding: '3px', bgcolor: 'darkgray' }} />
                                </TableRow>

                                {/* Header Row */}
                                <TableRow>
                                  <TableCell colSpan={2} sx={{ padding: '8px', bgcolor: 'lightgray' }}>
                                    <Typography component="span" sx={{ fontWeight: 'bold' }}>
                                      HCC {recap.HCC} - {recap.HCCDescription}
                                    </Typography>
                                    <br />
                                    <span style={{ fontSize: '0.8em', color: '#555555' }}>
                                      Last Assessed: {latestEvidence || 'N/A'}
                                    </span>
                                  </TableCell>
                                </TableRow>

                                {/* Action Row with View + Resolve buttons side-by-side */}
                                <TableRow>
                                  <TableCell colSpan={2} align="right" sx={{ padding: '6px', bgcolor: '#fdedec' }}>
                                    <Box display="flex" justifyContent="flex-end" gap={1}>
                                      <Button
                                        variant="contained"
                                        size="small"
                                        sx={{
                                          backgroundColor: '#ADD8E6',
                                          color: '#000',
                                          fontSize: '12px',
                                          padding: '4px 8px',
                                          '&:hover': { backgroundColor: '#BFEFFF' },
                                        }}
                                        onClick={() =>
                                          handleViewEvidenceModal(recap.Evidences, `${recap.MessageId} - ${recap.HCCDescription}`)
                                        }
                                      >
                                        View Evidence
                                      </Button>
                                      {recap.Feedbacks?.length > 0 && (
                                        <Button
                                          variant="contained"
                                          size="small"
                                          sx={{
                                            backgroundColor: '#ADD8E6',
                                            color: '#000',
                                            fontSize: '12px',
                                            padding: '4px 8px',
                                            '&:hover': { backgroundColor: '#BFEFFF' },
                                          }}
                                          onClick={() => handleViewNotes(recap.Feedbacks)}
                                        >
                                          Notes
                                        </Button>
                                      )}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            );
                        } else if (recap.ModuleName === "Clinical_Leadership") {
                          return (<TableRow key={recap.MessageId}>
                            <TableCell sx={{ padding: '4px', bgcolor: "#fdedec" }}>
                              <strong>{recap.CreatedBy}</strong>: {recap.Description}
                            </TableCell>
                            <TableCell sx={{ padding: '4px', bgcolor: "#fdedec" }} align="right">
                              <FormControl variant="standard" sx={{ m: 1, minWidth: 46 }}>
                                {recap.Feedbacks?.length > 0 && (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    sx={{
                                      backgroundColor: '#BBBBBB',
                                      color: '#000',
                                      fontSize: '12px',
                                      padding: '4px 8px',
                                      '&:hover': { backgroundColor: '#BFEFFF' },
                                      marginBottom: '5px'
                                    }}
                                    onClick={() => handleViewNotes(recap.Feedbacks)}
                                  >
                                    Notes
                                  </Button>
                                )}
                              </FormControl>
                            </TableCell>
                          </TableRow>);
                        }
                        return null; // If no matching ModuleName, return null  
                      })
                      }
                      </React.Fragment>)
                    )}
                    
                </TableBody>
            </Table>
        </TableContainer>
        <Divider sx={{ height:10 }}/></div>)
    } 

    if (patient.ActiveCoding === 1) {
      codingComponent = (<div><Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          bgcolor="#b8503a"
          color="white"
          p={1}
          borderRadius="4px 4px 0 0"
      >
          Coder Suggested Actions
      </Box>


      <TableContainer component={Paper}>
        <Table aria-label="simple table">
          <TableBody>
            {hccCoding.map((dx, index) => (
              <React.Fragment key={`dx-${index}`}>
                {/* Spacer Row */}
                <TableRow>
                  <TableCell sx={{ padding: '3px', bgcolor: 'darkgray' }} />
                </TableRow>

                {/* Header Row — assuming the first gap represents the group */}
                {dx.length > 0 && (
                  <TableRow>
                    <TableCell sx={{ padding: '8px', bgcolor: 'lightgray' }}>
                      <Typography component="span" sx={{ fontWeight: 'bold' }}>
                        {dx[0].DiagnosisCode} - {dx[0].DiagnosisDescription} (HCC: {dx[0].HCC.split(' - ')[0]})
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {/* Gap Rows */}
                {dx.map(gap => (
                  <TableRow key={gap.MessageId}>
                    <TableCell sx={{ padding: '4px' }}>
                      <strong>{gap.CreatedBy}</strong>: {gap.Description}
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>


      </div>)
  } 

    return (
        <div>
            <Accordion defaultExpanded>
                <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ backgroundColor: '#d5dbdb', margin: '0 !important', minHeight: 'unset !important', '& .MuiAccordionSummary-content': { margin: '5px 0 !important' } }}
                >
                    <Typography variant="h6">Disease Detection</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: '#eaeded', padding: '12px' }}>
                    {gapComponent}
                    {recCompoent}
                    {codingComponent}
                </AccordionDetails>
            </Accordion>
            <Divider sx={{ height:15 }}/>
            <Feedback open={isFeedbackOpen} onClose={handleCloseFeedback} patientId={patientId} username={username} token={token} module={feedbackConfig} />
            <Dialog open={openNotesModal} onClose={handleCloseNotesModal} maxWidth="sm" fullWidth>
              <DialogTitle>Follow Up Note History</DialogTitle>
              <DialogContent>
                  <Table>
                      <TableBody>
                          {activeFeedbacks.map((fb, index) => (
                              <TableRow key={index}>
                                  <TableCell>{fb.feedback}<br /><span style={{ fontSize: '0.8em', color: '#555555' }}>By: {fb.feedback_by} on {fb.feedback_at}</span></TableCell>
                              </TableRow>
                          ))}
                      </TableBody>
                  </Table>
              </DialogContent>
              <DialogActions>
                  <Button onClick={handleCloseNotesModal}>Close</Button>
              </DialogActions>
            </Dialog>
            <Dialog open={openEvidenceModal} onClose={handleCloseEvidenceModal} maxWidth="md" fullWidth>
              <DialogTitle>Evidence for {activeDiagnosisHeader}</DialogTitle>
              <DialogContent dividers>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Detail</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeEvidences.map((ev, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{ev.detail || '(No detail)'}</TableCell>
                        <TableCell>{ev.diagnosis_code}</TableCell>
                        <TableCell>{ev.detected_date}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseEvidenceModal}>Close</Button>
              </DialogActions>
            </Dialog>
        </div>
    );
}

export default CoralDiseaseDetection;
